import { maxWrongForDifficulty } from "./hangmanCategories.js";

/**
 * @typedef {{ id: string, name: string, color?: string }} HangmanPlayer
 * @typedef {{
 *   puzzle: string,
 *   players: HangmanPlayer[],
 *   scores: Record<string, number>,
 *   turnIndex: number,
 *   guessedLetters: string[],
 *   wrongCount: number,
 *   maxWrong: number,
 *   skippedIds: string[],
 *   status: 'playing' | 'won' | 'lost',
 *   round: number,
 *   totalRounds: number,
 *   lastAction?: { type: string, playerId: string, letter?: string, delta?: number }
 * }} HangmanRoundState
 */

export function normalizeGuessText(text) {
  return String(text || "")
    .toUpperCase()
    .replace(/['’`-]/g, "")
    .replace(/[^A-Z\s]/g, " ")
    .replace(/\s+/g, " ")
    .trim();
}

export function createScores(players) {
  const scores = {};
  for (const p of players) scores[p.id] = 0;
  return scores;
}

/**
 * Start a new round (keeps cumulative scores across rounds).
 * Skipped players reset each round so they can try again next word.
 */
export function startRound({
  puzzle,
  players,
  scores,
  round,
  totalRounds,
  turnIndex = 0,
  maxWrong,
  difficulty = "medium",
  skippedIds = []
}) {
  const normalized = normalizeGuessText(puzzle);
  if (!normalized) throw new Error("Invalid puzzle");
  if (!players?.length) throw new Error("Need at least one player");

  const base = {
    puzzle: normalized,
    players: players.map((p) => ({ ...p })),
    scores: scores ? { ...scores } : createScores(players),
    turnIndex: turnIndex % players.length,
    guessedLetters: [],
    wrongCount: 0,
    maxWrong: maxWrong || maxWrongForDifficulty(difficulty),
    skippedIds: [],
    status: "playing",
    round,
    totalRounds,
    lastAction: null
  };

  // Land on a non-skipped player (skippedIds cleared above; keep param unused for API clarity)
  void skippedIds;
  return {
    ...base,
    turnIndex: nextEligibleTurnIndex(base, turnIndex % players.length)
  };
}

export function currentPlayer(state) {
  return state.players[state.turnIndex] || state.players[0];
}

export function isSkipped(state, playerId) {
  return (state.skippedIds || []).includes(playerId);
}

export function getMaskedChars(puzzle, guessedLetters) {
  const guessed = new Set(guessedLetters);
  return puzzle.split("").map((ch) => {
    if (ch === " ") return { type: "space", char: " " };
    if (guessed.has(ch)) return { type: "letter", char: ch, revealed: true };
    return { type: "letter", char: ch, revealed: false };
  });
}

export function isPuzzleSolved(puzzle, guessedLetters) {
  const guessed = new Set(guessedLetters);
  for (const ch of puzzle) {
    if (ch !== " " && !guessed.has(ch)) return false;
  }
  return true;
}

function addScore(scores, playerId, delta) {
  const next = { ...scores };
  const cur = next[playerId] || 0;
  next[playerId] = Math.max(0, cur + delta);
  return next;
}

function zeroScore(scores, playerId) {
  return { ...scores, [playerId]: 0 };
}

/** Next eligible turn index starting from `fromIndex` (inclusive). */
function nextEligibleTurnIndex(state, fromIndex) {
  const skipped = new Set(state.skippedIds || []);
  const n = state.players.length;
  let i = ((fromIndex % n) + n) % n;
  for (let step = 0; step < n; step += 1) {
    const id = state.players[i]?.id;
    if (id && !skipped.has(id)) return i;
    i = (i + 1) % n;
  }
  return ((fromIndex % n) + n) % n;
}

function advanceTurn(state) {
  const n = state.players.length;
  const start = (state.turnIndex + 1) % n;
  return nextEligibleTurnIndex(state, start);
}

/**
 * Guess a single letter for the current player.
 */
export function guessLetter(state, letter) {
  if (state.status !== "playing") return state;
  const player = currentPlayer(state);
  if (isSkipped(state, player.id)) {
    return { ...state, turnIndex: advanceTurn(state) };
  }

  const L = String(letter || "").toUpperCase();
  if (!/^[A-Z]$/.test(L)) return state;
  if (state.guessedLetters.includes(L)) return state;

  const guessedLetters = [...state.guessedLetters, L];
  const occurrences = state.puzzle.split("").filter((c) => c === L).length;

  if (occurrences > 0) {
    let scores = addScore(state.scores, player.id, occurrences);
    const solved = isPuzzleSolved(state.puzzle, guessedLetters);
    if (solved) {
      scores = addScore(scores, player.id, 5);
      return {
        ...state,
        guessedLetters,
        scores,
        status: "won",
        lastAction: {
          type: "letter-correct-win",
          playerId: player.id,
          letter: L,
          delta: occurrences + 5
        }
      };
    }
    return {
      ...state,
      guessedLetters,
      scores,
      turnIndex: advanceTurn(state),
      lastAction: {
        type: "letter-correct",
        playerId: player.id,
        letter: L,
        delta: occurrences
      }
    };
  }

  const wrongCount = state.wrongCount + 1;
  const limit = state.maxWrong || maxWrongForDifficulty("medium");
  const lost = wrongCount >= limit;
  return {
    ...state,
    guessedLetters,
    wrongCount,
    status: lost ? "lost" : "playing",
    turnIndex: lost ? state.turnIndex : advanceTurn(state),
    lastAction: {
      type: lost ? "letter-wrong-lose" : "letter-wrong",
      playerId: player.id,
      letter: L,
      delta: 0
    }
  };
}

/**
 * Solve attempt (full word/phrase) for the current player.
 * Wrong solve: zero their points, skip them for the rest of the round, advance turn.
 * Does not advance the hangman figure.
 */
export function guessWord(state, guessText) {
  if (state.status !== "playing") return state;
  const player = currentPlayer(state);
  if (isSkipped(state, player.id)) {
    return { ...state, turnIndex: advanceTurn(state) };
  }

  const guess = normalizeGuessText(guessText);
  if (!guess) return state;

  if (guess === state.puzzle) {
    const scores = addScore(state.scores, player.id, 5);
    const allLetters = [...new Set(state.puzzle.replace(/ /g, "").split(""))];
    return {
      ...state,
      guessedLetters: [...new Set([...state.guessedLetters, ...allLetters])],
      scores,
      status: "won",
      lastAction: {
        type: "word-correct",
        playerId: player.id,
        delta: 5
      }
    };
  }

  const skippedIds = [...new Set([...(state.skippedIds || []), player.id])];
  const scores = zeroScore(state.scores, player.id);
  const nextState = {
    ...state,
    scores,
    skippedIds,
    lastAction: {
      type: "word-wrong-skip",
      playerId: player.id,
      delta: -(state.scores[player.id] || 0)
    }
  };
  return {
    ...nextState,
    turnIndex: advanceTurn(nextState)
  };
}

export function sortedLeaderboard(state) {
  return [...state.players]
    .map((p) => ({
      ...p,
      score: state.scores[p.id] || 0,
      skipped: isSkipped(state, p.id)
    }))
    .sort((a, b) => b.score - a.score || a.name.localeCompare(b.name));
}
